class MovaFreeShippingProgress extends HTMLElement {
  #observer;
  #scope;
  #scheduledFrame;
  #scheduledTimeout;
  #lastAmount;

  connectedCallback() {
    if (this.dataset.source !== 'product') return;

    this.#scope = this.closest('.shopify-section') || document.body;
    this.#scope.addEventListener('input', this.#handleInput);
    this.#scope.addEventListener('change', this.#handleInput);

    this.#observer = new MutationObserver(this.#scheduleUpdate);
    this.#observer.observe(this.#scope, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    this.#update();
  }

  disconnectedCallback() {
    this.#scope?.removeEventListener('input', this.#handleInput);
    this.#scope?.removeEventListener('change', this.#handleInput);
    this.#observer?.disconnect();

    if (this.#scheduledFrame) {
      cancelAnimationFrame(this.#scheduledFrame);
      clearTimeout(this.#scheduledTimeout);
      this.#scheduledFrame = undefined;
    }
  }

  #handleInput = () => {
    this.#scheduleUpdate();
  };

  #scheduleUpdate = () => {
    if (this.#scheduledFrame) return;

    // requestAnimationFrame puede quedar en pausa en el navegador de Instagram:
    // el setTimeout asegura que la barra se actualice igual.
    const run = () => {
      if (!this.#scheduledFrame) return;
      cancelAnimationFrame(this.#scheduledFrame);
      clearTimeout(this.#scheduledTimeout);
      this.#scheduledFrame = undefined;
      this.#update();
    };
    this.#scheduledFrame = requestAnimationFrame(run);
    this.#scheduledTimeout = setTimeout(run, 120);
  };

  #update() {
    const threshold = Number(this.dataset.threshold) || 34900;
    const currentAmount = this.#getCurrentAmount();

    if (!Number.isFinite(currentAmount) || currentAmount === this.#lastAmount) return;
    this.#lastAmount = currentAmount;

    const remaining = Math.max(0, threshold - currentAmount);
    const progress = Math.max(0, Math.min(100, (currentAmount / threshold) * 100));
    const unlocked = currentAmount >= threshold;
    const formattedRemaining = this.#formatMoney(remaining);
    const status = this.querySelector('[data-free-shipping-status]');
    const prefix = this.querySelector('[data-free-shipping-prefix]');
    const remainingValue = this.querySelector('[data-free-shipping-remaining]');
    const bar = this.querySelector('[data-free-shipping-bar]');

    this.style.setProperty('--mova-free-shipping-progress', `${progress.toFixed(2)}%`);

    if (status) {
      status.textContent = unlocked
        ? 'Tu pedido tiene envío gratis'
        : 'Te falta un poco para el envío gratis';
    }

    if (prefix) {
      prefix.textContent = unlocked ? 'Beneficio desbloqueado' : 'Te faltan';
    }

    if (remainingValue) {
      remainingValue.textContent = formattedRemaining;
      remainingValue.hidden = unlocked;
    }

    if (bar) {
      bar.setAttribute('aria-valuenow', String(Math.round(progress)));
      bar.setAttribute('aria-valuetext', unlocked ? 'Envío gratis desbloqueado' : `Faltan ${formattedRemaining}`);
    }
  }

  #getCurrentAmount() {
    const bundleTotal = this.#getBundleTotal();
    if (bundleTotal !== null) return bundleTotal;

    const productForm = this.closest('product-form-component');
    const quantityInput =
      productForm?.querySelector('input[name="quantity"]') ||
      this.#scope?.querySelector('input[name="quantity"]');
    const quantity = Math.max(1, Number(quantityInput?.value) || 1);
    const unitPrice = Number(this.dataset.unitPrice) || Number(this.dataset.currentAmount) || 0;

    return unitPrice * quantity;
  }

  #getBundleTotal() {
    const bundleTotalElement =
      this.#scope?.querySelector(
        '.bx-widget-container .js-bx-total-price, .js-bx-total-price, .bx-widget-container .bx-footer-right, .bx-footer-right, .bx-widget-container .bx-footer, .bx-footer'
      ) ||
      document.querySelector(
        '.bx-widget-container .js-bx-total-price, .js-bx-total-price, .bx-widget-container .bx-footer-right, .bx-footer-right, .bx-widget-container .bx-footer, .bx-footer'
      );
    const matches = bundleTotalElement?.textContent?.match(/[\d][\d.,]*/g);
    const total = matches?.at(-1);

    if (!total) return null;

    const parsedTotal = Number(total.replace(/[.,]/g, ''));
    return Number.isFinite(parsedTotal) ? parsedTotal : null;
  }

  #formatMoney(amount) {
    const currency = this.dataset.currency || 'CLP';

    try {
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(Math.round(amount));
    } catch (_error) {
      return `$${Math.round(amount).toLocaleString('es-CL')}`;
    }
  }
}

if (!customElements.get('mova-free-shipping-progress')) {
  customElements.define('mova-free-shipping-progress', MovaFreeShippingProgress);
}
